<template>
  <vue-resizable
    v-slot="{ style }"
    drag-selector=".cursor-grab"
    v-bind="activeLocalStorageOptions"
    :min-width="callWidthAndHeight.minWidth"
    :min-height="callWidthAndHeight.minHeight"
    @mount="updatePosition"
    @resize:end="updatePosition"
    @drag:end="updatePosition"
  >
    <div
      ref="resizableElement"
      :style="style"
      class="call-log-dialog rounded-md bg-white"
      :class="isMinimized ? 'max-w-55' : 'flex h-full flex-col'"
      data-cy="call-log-dialog"
    >
      <div
        class="call-log-dialog-header flex justify-between space-x-2 rounded-t-md bg-dark p-4"
        :class="{ 'max-w-55 rounded-b-md': isMinimized }"
        data-cy="call-log-dialog-header"
      >
        <div class="flex grow cursor-grab items-center space-x-4">
          <span
            :title="
              value?.data?.call_sessionable?.business?.business_legal_name
            "
            class="heading grow cursor-grab overflow-hidden text-ellipsis whitespace-nowrap text-white"
            data-cy="call-dialog-title"
            @dblclick="toggleMinimize"
          >
            {{ value?.data?.call_sessionable?.business?.business_legal_name }}
          </span>
          <a v-if="dealRoute" :href="dealRoute" target="_blank">
            <icon-base :icon="IconVisit" class="hover:text-white" />
          </a>
        </div>
        <div class="flex space-x-2">
          <icon-base
            v-if="isMinimized"
            :icon="IconMaximize"
            icon-name="Maximize"
            class="cursor-pointer text-white"
            data-cy="maximize-call-log-dialog"
            @click="toggleMinimize"
          />
          <icon-base
            v-else
            :icon="IconSubtract"
            view-box="0 -5 16 16"
            icon-name="Minimize"
            class="cursor-pointer text-white"
            data-cy="minimize-call-log-dialog"
            @click="toggleMinimize"
          />
          <icon-base
            :icon="IconClose"
            icon-name="Close"
            class="cursor-pointer"
            @click="emit('close-dialog')"
          />
        </div>
      </div>
      <create-call-note-form
        v-show="!isMinimized"
        class="flex flex-1 flex-col rounded-none border-0"
        :call-log="value"
        data-cy="create-call-note-form"
        @call-note:edit="handleNoteCreation"
      >
        <template #supplemental>
          <div
            class="flex items-center justify-between bg-grey-hover px-3 pt-1 text-xs"
          >
            <span class="line-clamp-1 shrink" data-cy="client-name">
              {{ value?.data?.call_sessionable?.client_name }}
            </span>
            <span class="line-clamp-1 shrink" data-cy="workflow-name">
              {{ workflowName }}
            </span>
          </div>
        </template>
      </create-call-note-form>
    </div>
  </vue-resizable>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import VueResizable from "vue-resizable";

import { DEFAULT_CALL_MODAL_OPTIONS } from "@/helpers/constants/deals";
import { CALL_DIALOG } from "@/helpers/constants/resizable";
import { useLocalStorageSetting } from "@/hooks/options";
import { useResizable } from "@/hooks/resizable";

import CreateCallNoteForm from "@/components/communicationLogs/forms/CreateCallNoteForm.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconMaximize from "@/components/icons/IconMaximize.vue";
import IconSubtract from "@/components/icons/IconSubtract.vue";
import IconVisit from "@/components/icons/IconVisit.vue";

import type { CallLog } from "@/models/communicationLogs";

const emit = defineEmits<{
  "close-dialog": [];
}>();

const props = withDefaults(
  defineProps<{
    value?: CallLog;
    dealId?: string;
    workflowName?: string;
  }>(),
  {
    value: undefined,
    dealId: "",
    workflowName: "",
  }
);

const activeLocalStorageOptions = useLocalStorageSetting(
  "callModalOptions",
  DEFAULT_CALL_MODAL_OPTIONS
);

const callWidthAndHeight = ref({
  width: DEFAULT_CALL_MODAL_OPTIONS.width,
  height: DEFAULT_CALL_MODAL_OPTIONS.height,
  minWidth: CALL_DIALOG.minWidth,
  minHeight: CALL_DIALOG.minHeight,
  minimizedWidth: CALL_DIALOG.minimizedWidth,
  minimizedHeight: CALL_DIALOG.minimizedHeight,
});

const isMinimized = ref(false);
const resizableElement = ref<HTMLDivElement | null>(null);

const { updatePosition } = useResizable(
  activeLocalStorageOptions,
  resizableElement
);

const dealRoute = computed(() => {
  if (!props.dealId) {
    return "";
  }
  return `/deals/${props.dealId}/progress`;
});

const toggleMinimize = () => {
  if (!isMinimized.value) {
    const { width, height } = activeLocalStorageOptions.value;
    activeLocalStorageOptions.value.restoreHeight = height;
    activeLocalStorageOptions.value.restoreWidth = width;
  }
  isMinimized.value = !isMinimized.value;

  const { width, height, minimizedWidth, minimizedHeight } =
    callWidthAndHeight.value;

  const { restoreWidth, restoreHeight, left, top } =
    activeLocalStorageOptions.value;

  const setWidth = isMinimized.value ? minimizedWidth : restoreWidth || width;
  const setHeight = isMinimized.value
    ? minimizedHeight
    : restoreHeight || height;

  updatePosition({
    left,
    top,
    width: setWidth,
    height: setHeight,
  });
};

const handleNoteCreation = () => {
  emit("close-dialog");
};

defineExpose({
  resizableElement,
});
</script>

<style>
@reference "@/assets/tailwind.css";

.call-log-dialog .note-textarea {
  border-radius: 0;
  outline: none;
}
.call-log-dialog .note-textarea:focus {
  box-shadow: none;
}

@media (display-mode: picture-in-picture) {
  .call-log-dialog,
  .call-log-dialog-header {
    @apply rounded-t-none;
  }
}
</style>
